import { BRANDS, DEFAULT_CUSTOMER, isCustomer, type BrandConfig, type Customer } from './src/brands';
import { initI18n } from './src/i18n/i18n';

interface EnvConfig {
  backendUrl: string;
  cognitoUserPoolId: string;
  cognitoUserPoolClientId: string;
  customer: Customer;
  brand: BrandConfig;
}

// VITE_CUSTOMER is a build-time choice - Stroomkaart and Bristol get
// separate Docker images via the pipeline's --build-arg. The brand bundle
// (logos, title, colour, currency, languages) is therefore baked into the
// build output: `envConfig.brand` resolves synchronously at module load
// and the HTML files have brand-specific %VITE_BRAND_*% placeholders
// substituted at build time, so the first paint is already correct. The
// runtime `loadConfig()` only carries env-specific values (backend URL,
// Cognito IDs) that vary between tst/acc/prd within the same image.
const buildTimeCustomer = (() => {
  const raw = import.meta.env.VITE_CUSTOMER as string | undefined;
  return isCustomer(raw) ? raw : DEFAULT_CUSTOMER;
})();

export const envConfig: EnvConfig = {
  backendUrl: (import.meta.env.VITE_BACKEND_URL as string | undefined) ?? '',
  cognitoUserPoolId: (import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined) ?? '',
  cognitoUserPoolClientId: (import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID as string | undefined) ?? '',
  customer: buildTimeCustomer,
  brand: BRANDS[buildTimeCustomer],
};

export async function loadConfig(): Promise<void> {
  try {
    const response = await fetch('/config.json');
    if (response.ok) {
      const config = await response.json();
      if (config.VITE_BACKEND_URL) envConfig.backendUrl = config.VITE_BACKEND_URL;
      if (config.VITE_COGNITO_USER_POOL_ID) envConfig.cognitoUserPoolId = config.VITE_COGNITO_USER_POOL_ID;
      if (config.VITE_COGNITO_USER_POOL_CLIENT_ID) envConfig.cognitoUserPoolClientId = config.VITE_COGNITO_USER_POOL_CLIENT_ID;
    }
  } catch {
    // config.json absent (local dev) - env-injected defaults are used.
  }
  initI18n();
}
